import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import Check from "@mui/icons-material/Check";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { UpdateIsCustomerReviewed, getNewCustomerData } from "../analyzeSlice";
import muiConfig from '../../../assets/MuiConfig.json'

function normalizeScenarioTable(raw) {
  if (!raw) return [];
  return Array.isArray(raw) ? raw : [];
}

function rowScenarioId(row) {
  return row?.scenarioID ?? row?.ScenarioID;
}

function rowCustomerId(row) {
  return row?.customerID ?? row?.CustomerID;
}

function rowScenarioName(row) {
  return row?.scenarioName ?? row?.ScenarioName ?? "";
}

/** Scenarios this customer appears in only, unique by scenario ID, sorted by name. */
function buildMemberScenariosForCustomer(scenarioTable, customerId) {
  if (customerId == null) return [];
  const target = String(customerId);
  const byId = new Map();
  for (const row of scenarioTable) {
    const cid = rowCustomerId(row);
    if (cid == null || String(cid) !== target) continue;
    const sid = rowScenarioId(row);
    if (sid == null) continue;
    const idKey = String(sid);
    if (!byId.has(idKey)) {
      byId.set(idKey, {
        scenarioID: sid,
        scenarioName: rowScenarioName(row) || `Scenario ${sid}`,
      });
    }
  }
  return [...byId.values()].sort((a, b) =>
    String(a.scenarioName).localeCompare(String(b.scenarioName), undefined, {
      sensitivity: "base",
    })
  );
}

function ScenarioMembershipPanel({ row, memberScenarios }) {
  const n = memberScenarios.length;
  if (n === 0) {
    return null;
  }

  const displayName = row?.customerName ?? "";

  return (
    <Box sx={{ p: 2, bgcolor: "background.paper", width: "100%", maxWidth: 380 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Scenario Membership
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {displayName} — {n} {n === 1 ? "scenario" : "scenarios"}
      </Typography>
      <Stack spacing={0.25} sx={{ maxHeight: 280, overflow: "auto", pr: 0.5 }}>
        {memberScenarios.map((s) => (
          <Box
            key={String(s.scenarioID)}
            sx={(theme) => ({
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderRadius: 1,
              px: 1.25,
              py: 0.5,
              border: 1,
              borderColor: alpha(theme.palette.success.main, 0.18),
              bgcolor: alpha(theme.palette.success.main, 0.06),
              color: "text.primary",
            })}
          >
            <Typography
              variant="body2"
              sx={{ pr: 1, wordBreak: "break-word", flex: 1, minWidth: 0 }}
            >
              {s.scenarioName}
            </Typography>
            <Check
              sx={(theme) => ({
                color: alpha(theme.palette.success.dark, 0.85),
                fontSize: 18,
                flexShrink: 0,
              })}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default function Table() {
    
  const autherization = localStorage.getItem("data");
  const data = JSON.parse(autherization);
  let setAccess = true;
  if (data?.restrictedAccess == 0) setAccess = true;
  else setAccess = false;

  const isRestrictedAccess = setAccess;
  console.log(isRestrictedAccess)
  const apiRef = useGridApiRef();
  const dispatch = useDispatch();
  const state = useSelector((store) => store.analyze);
  const {
    newCustomerDataPrepared,
    selectedCountry,
    selectedCountryID,
    duration,
    newCustScenCustData,
  } = state;
  const { rows, columns } = newCustomerDataPrepared;
  const [tableRows, setTableRows] = useState(rows);
  const [rowSelectionModel, setRowSelectionModel] = React.useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [isReviewedSelected, setIsReviewedSelected] = useState("");
  const [searchText, setSearchText] = useState("");
  /** 'all' after fresh data (Display); 'yes' / 'no' narrow by scenario membership count. */
  const [scenarioScopeFilter, setScenarioScopeFilter] = useState("all");
  const prevRowsRef = useRef(rows);
  /** Popover avoids DataGrid detail panel full-width layout and row height expansion. */
  const [scenarioPopover, setScenarioPopover] = useState(null);
  const selectWidth = "200";
  const selectHeight = "35";

  const scenarioTable = useMemo(
    () => normalizeScenarioTable(newCustScenCustData),
    [newCustScenCustData]
  );

  const switchClass = "custom-switch red-thumb red-track";

  const scenarioCountByCustomer = useMemo(() => {
    const byCustomer = new Map();
    for (const row of scenarioTable) {
      const cid = rowCustomerId(row);
      const sid = rowScenarioId(row);
      if (cid == null || sid == null) continue;
      const key = String(cid);
      if (!byCustomer.has(key)) byCustomer.set(key, new Set());
      byCustomer.get(key).add(String(sid));
    }
    const counts = new Map();
    byCustomer.forEach((set, key) => counts.set(key, set.size));
    return counts;
  }, [scenarioTable]);

  const gridColumns = useMemo(() => {
    const scenarioScopeColumn = {
      field: "scenarioScope",
      headerName: "Scenario Scope",
      flex: 1,
      minWidth: 200,
      filterable: false,
      disableColumnMenu: true,
      valueGetter: (params) =>
        scenarioCountByCustomer.get(String(params.row.customerID)) ?? 0,
      sortComparator: (v1, v2) => v1 - v2,
      renderHeader: () => (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{ minWidth: 0, width: "100%", py: 0.25 }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}
          >
            Scenario Scope
          </Typography>
          <Switch
            size="small"
            indeterminate={scenarioScopeFilter === "all"}
            checked={scenarioScopeFilter === "yes"}
            onClick={(e) => e.stopPropagation()}
            onChange={(_, checked) =>
              setScenarioScopeFilter(checked ? "yes" : "no")
            }
            inputProps={{
              "aria-label": "Filter rows by scenario scope: all, yes, or no",
            }}
            className={switchClass}
            sx={{ flexShrink: 0 }}
          />
        </Stack>
      ),
      renderCell: (params) => {
        const rowId = params.row.customerID;
        const count = scenarioCountByCustomer.get(String(rowId)) ?? 0;
        const inScope = count > 0;
        const rowKey = String(rowId);
        const expanded =
          scenarioPopover &&
          String(scenarioPopover.row.customerID) === rowKey;
        const statusLabel = inScope ? "Yes" : "No";

        const pillBg = inScope ? "rgba(46, 125, 50, 0.12)" : "grey.200";
        const accent = inScope ? "success.main" : "text.secondary";
        const circleBg = inScope ? "success.dark" : "grey.700";

        return (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              if (count === 0) return;
              const anchorEl = e.currentTarget;
              if (
                scenarioPopover &&
                String(scenarioPopover.row.customerID) === rowKey
              ) {
                setScenarioPopover(null);
              } else {
                setScenarioPopover({ anchorEl, row: params.row });
              }
            }}
            sx={{
              cursor: count === 0 ? "default" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              py: 0.5,
              px: 1,
              borderRadius: 10,
              bgcolor: pillBg,
              border: 1,
              borderColor: inScope ? "success.light" : "divider",
              maxWidth: "100%",
            }}
          >
            <Box
              sx={{
                minWidth: 22,
                height: 22,
                borderRadius: "50%",
                bgcolor: circleBg,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {count}
            </Box>
            <Typography variant="body2" sx={{ color: accent, fontWeight: 600 }}>
              {statusLabel}
            </Typography>
            <KeyboardArrowDown
              sx={{
                fontSize: 20,
                color: accent,
                transform: expanded ? "rotate(180deg)" : "none",
                transition: "transform 0.2s",
              }}
            />
          </Box>
        );
      },
    };

    return [...columns, scenarioScopeColumn];
  }, [columns, scenarioCountByCustomer, scenarioPopover, scenarioScopeFilter]);

  const handleUpdate = () => {
    const finalArray = rowSelectionModel.map((id) => {
      let updatedTrustValues = apiRef.current.getRowWithUpdatedValues(id);
      return updatedTrustValues;
    });

    const obj = {
      countryID: selectedCountryID,
      duration: duration,
    };

    console.log(finalArray);
    dispatch(UpdateIsCustomerReviewed(finalArray)).then(() => {
      dispatch(getNewCustomerData(obj));
    });

    setIsReviewedSelected("");
    setSearchText("");
    setPaginationModel({
      pageSize: 20,
      page: 0,
    })
  };
  // useEffect(() => {
  // 	setTableRows(rows);
  // }, [rows])

  const handleSearchText = (e) => {
    const value = e.target.value;
    setSearchText(value);

    // const searchLowercase = value.toLowerCase();
    // const filteredRows = rows?.filter((row) => {
    // 	return (
    // 		row.customerName.toLowerCase().includes(searchLowercase) ||
    // 		row.customerNumber.toLowerCase().includes(searchLowercase) ||
    // 		row.customerLocation.toLowerCase().includes(searchLowercase) ||
    // 		row.customerType.toLowerCase().includes(searchLowercase) ||
    // 		row.partnerType.toLowerCase().includes(searchLowercase)
    // 	);
    // });
    // setTableRows(filteredRows);
  };

  //   useEffect(() => {
  // 	if (searchText === '') {
  // 	  setTableRows(rows);
  // 	} else {
  // 	  const searchLowercase = searchText.toLowerCase();
  // 	  const filteredRows = rows.filter((row) => {
  // 		return (
  // 		  row.customerName.toLowerCase().includes(searchLowercase) ||
  // 		  row.customerNumber.toLowerCase().includes(searchLowercase) ||
  // 		  row.customerLocation.toLowerCase().includes(searchLowercase) ||
  // 		  row.customerType.toLowerCase().includes(searchLowercase) ||
  // 		  row.partnerType.toLowerCase().includes(searchLowercase)
  // 		);
  // 	  });
  // 	  setTableRows(filteredRows);
  // 	}
  //   }, [searchText, rows]);

  //   useEffect(() => {
  // 	if (isReviewedSelected === 'Reviewed' || isReviewedSelected === 'Not Reviewed') {
  // 	  const filteredRows = rows.filter((item) => {
  // 		return isReviewedSelected === 'Reviewed'
  // 		  ? item.isReviewed === 1
  // 		  : item.isReviewed === 0;
  // 	  });
  // 	  setTableRows(filteredRows);
  // 	} else {
  // 	  setTableRows(rows);
  // 	}
  //   }, [isReviewedSelected, rows]);

  useEffect(() => {
    const isNewRowSet = prevRowsRef.current !== rows;
    prevRowsRef.current = rows;

    const scopeForFilter =
      isNewRowSet ? "all" : scenarioScopeFilter;
    if (isNewRowSet && scenarioScopeFilter !== "all") {
      setScenarioScopeFilter("all");
    }

    let filteredRows = rows;

    if (
      isReviewedSelected === "Reviewed" ||
      isReviewedSelected === "Not Reviewed"
    ) {
      filteredRows = filteredRows.filter((item) => {
        return isReviewedSelected === "Reviewed"
          ? item.isReviewed === 1
          : item.isReviewed === 0;
      });
    }

    if (scopeForFilter === "yes") {
      filteredRows = filteredRows.filter((row) => {
        const count =
          scenarioCountByCustomer.get(String(row.customerID)) ?? 0;
        return count > 0;
      });
    } else if (scopeForFilter === "no") {
      filteredRows = filteredRows.filter((row) => {
        const count =
          scenarioCountByCustomer.get(String(row.customerID)) ?? 0;
        return count === 0;
      });
    }

    if (searchText !== "") {
      const searchLowercase = searchText.toLowerCase();
      filteredRows = filteredRows.filter((row) => {
        return (
          row.customerName.toLowerCase().includes(searchLowercase) ||
          row.customerNumber.toLowerCase().includes(searchLowercase) ||
          row.customerLocation.toLowerCase().includes(searchLowercase) ||
          row.customerType.toLowerCase().includes(searchLowercase) ||
          row.partnerType.toLowerCase().includes(searchLowercase)
        );
      });
    }

    setTableRows(filteredRows);
  }, [
    isReviewedSelected,
    searchText,
    rows,
    scenarioScopeFilter,
    scenarioCountByCustomer,
  ]);

  useEffect(() => {
    setScenarioPopover(null);
  }, [tableRows]);

  console.log(rows);
  // useEffect(() => {
  // 	if (isReviewedSelected === "Reviewed" || isReviewedSelected === "Not Reviewed") {
  // 		setTableRows(prevVal => {
  // 			let val = rows?.filter(item => {
  // 				let retVal = false
  // 				if (isReviewedSelected === 'Reviewed') {
  // 					retVal = item.isReviewed === 1;
  // 				}
  // 				else if (isReviewedSelected === 'Not Reviewed') {
  // 					retVal = item.isReviewed === 0;
  // 				}
  // 				else {
  // 					return retVal
  // 				}
  // 				return retVal
  // 			})
  // 			return val
  // 		})
  // 	}
  // 	else {
  // 		setTableRows(rows)
  // 	}
  // }, [isReviewedSelected])

  // useEffect(() => {
  // 	if (searchText.length > 0) {
  // 		let val = rows?.filter(row => {
  // 			return (row.customerName.toLowerCase().includes(searchText.toLowerCase()) ||
  // 				row.customerNumber.toLowerCase().includes(searchText.toLowerCase()) ||
  // 				row.customerLocation.toLowerCase().includes(searchText.toLowerCase()) ||
  // 				row.customerType.toLowerCase().includes(searchText.toLowerCase()) ||
  // 				row.partnerType.toLowerCase().includes(searchText.toLowerCase()))
  // 		}
  // 		)
  // 		setTableRows(val)
  // 	}
  // 	else {
  // 		setTableRows(rows)
  // 	}
  // }, [searchText])

  return (
    <>
      <Box mb={3}>
        <Grid container columns={5} spacing={3}>
          <Grid item sm={1} lm={1}>
            <FormControl>
              <InputLabel></InputLabel>
              <Select
                value={isReviewedSelected ? isReviewedSelected : "All"}
                label=""
                sx={{
                  height: selectHeight,
                  width: selectWidth,
                  color: "black.main",
                  mt: 1,
                }}
                onChange={(e) => setIsReviewedSelected(e.target.value)}
              >
                <MenuItem key={1} value={"All"}>
                  {"All"}
                </MenuItem>
                <MenuItem key={2} value={"Reviewed"}>
                  {"Reviewed"}
                </MenuItem>
                <MenuItem key={3} value={"Not Reviewed"}>
                  {"Not Reviewed"}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item sm={2} lg={1.25}>
            <TextField
              variant="outlined"
              label="Filter.."
              value={searchText}
              onChange={handleSearchText}
              fullWidth
              size="small"
            ></TextField>
          </Grid>
        </Grid>
      </Box>
      <Box>
        <DataGridPro
          apiRef={apiRef}
          rows={tableRows}
          columns={gridColumns}
          getRowId={(row) => row?.customerID}
          initialState={{
            pagination: { paginationModel: { pageSize: 20, page: 0 } },
          }}
          pagination
          pageSizeOptions={[20, 50, 100, { value: muiConfig.DataGrid_All , label: 'All' }]}
          checkboxSelection={true}
          disableRowSelectionOnClick
          disableColumnMenu
          onRowSelectionModelChange={(newRowSelectionModel) => {
            console.log(newRowSelectionModel);
            setRowSelectionModel(newRowSelectionModel);
          }}
          sx={{ maxHeight: "74vh" }}
        />
        <Popover
          open={Boolean(scenarioPopover?.anchorEl)}
          anchorEl={scenarioPopover?.anchorEl}
          onClose={() => setScenarioPopover(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          marginThreshold={8}
          disableScrollLock
          PaperProps={{
            elevation: 8,
            sx: {
              maxWidth: 380,
              width: "min(380px, calc(100vw - 24px))",
              overflow: "hidden",
            },
          }}
        >
          {scenarioPopover?.row ? (
            <ScenarioMembershipPanel
              row={scenarioPopover.row}
              memberScenarios={buildMemberScenariosForCustomer(
                scenarioTable,
                scenarioPopover.row.customerID
              )}
            />
          ) : null}
        </Popover>
      </Box>

      {rowSelectionModel.length > 0 ? (
        <Box mt={3} justifyContent="flex-end" display="flex">
          <Button
            //startIcon={<SearchIcon />}
            variant="contained"
            disabled={!isRestrictedAccess}
						sx={{
							bgcolor: "darkred.light",
							color: "white.main",
							textTransform: "none",
							fontWeight: "bold",
							"&:hover": {
								bgcolor: "darkred.main",
							},
						}}            
            onClick={handleUpdate}
            size="large"
            // className="mui_Button"
            //disabled={Object.keys(selectedPeriod).length === 0 || loadQuotaDetails ? true : false}
          >
            Save Changes
          </Button>
        </Box>
      ) : (
        <></>
      )}
    </>
  );
}
